import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { FormatQuote, HourglassEmpty } from '@mui/icons-material';
import { TextQuestionInsight } from '../insightsTypes';
import QuestionCardSectionTitle from './QuestionCardSectionTitle';
import { NEUTRAL_COLOR } from '../insightsTheme';

// Per-question free-form detail: AI-clustered theme chips + verbatim customer quotes.
const FreeformQuestionCard: React.FC<{ insight: TextQuestionInsight }> = ({ insight }) => {
    const { themes, quotes, responseCount } = insight.overall;

    const hasNoThemesOrQuotes = themes.length === 0 && quotes.length === 0;

    // No answers match the current filters, so show the empty state.
    if (hasNoThemesOrQuotes && responseCount === 0) {
        return (
            <Stack direction="row" spacing={0.75} alignItems="center">
                <HourglassEmpty sx={{ fontSize: 15, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.disabled' }}>
                    No answers to this question in the selected date range and projects.
                </Typography>
            </Stack>
        );
    }

    // Answers exist, but no themes have been generated yet.
    // Show guidance instead of an empty expanded panel.
    if (hasNoThemesOrQuotes) {
        return (
            <Stack direction="row" spacing={0.75} alignItems="center">
                <HourglassEmpty sx={{ fontSize: 15, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.disabled' }}>
                    {responseCount} {responseCount === 1 ? 'response is' : 'responses are'} saved, but no themes have been generated from
                    them yet. Use Run analysis to generate them.
                </Typography>
            </Stack>
        );
    }

    return (
        <Box>
            {themes.length > 0 && (
                <Box sx={{ mb: quotes.length > 0 ? 1.5 : 0 }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {themes.map((theme) => (
                            <Tooltip
                                key={theme.label}
                                arrow
                                title={`${theme.callCount} of ${responseCount} ${responseCount === 1 ? 'answer' : 'answers'}`}
                            >
                                <Chip
                                    label={`${theme.label} ${Math.round(theme.percentOfAnswers)}%`}
                                    size="small"
                                    sx={{ fontWeight: 600, color: 'gray.100', bgcolor: NEUTRAL_COLOR, cursor: 'help' }}
                                />
                            </Tooltip>
                        ))}
                    </Stack>
                </Box>
            )}

            {quotes.length > 0 && (
                <Box>
                    <QuestionCardSectionTitle mb={1.5}>Quotes</QuestionCardSectionTitle>
                    <Stack spacing={1.25}>
                        {/* Use the index because the same quote can appear under multiple themes. */}
                        {quotes.map((quote, position) => (
                            <Stack key={`${String(position)}-${quote}`} direction="row" spacing={1} alignItems="flex-start">
                                <FormatQuote sx={{ fontSize: 18, color: 'gray.60', transform: 'scaleX(-1)', mt: '2px', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>
                                    {quote}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default FreeformQuestionCard;
